/**
 * pwa-update.ts
 * Bridges virtual:pwa-register's onNeedRefresh callback to a reactive flag
 * a UI component can read, and exposes a way to apply the update — ONLY
 * ever triggered by an explicit user tap (see main.ts / vite.config.ts
 * header comments: registerType is 'prompt', not 'autoUpdate', on purpose —
 * a silent auto-reload once stranded a login mid-flow). Without this,
 * `registerType: 'prompt'` left updates completely unreachable (no
 * onNeedRefresh handler was wired at all) — a new service worker would sit
 * waiting forever unless the user manually cleared the PWA's storage, so a
 * shipped fix could go unseen indefinitely.
 *
 * `scheduleUpdateChecks` closes a second gap in the same story: even with
 * onNeedRefresh wired, `registerSW({ immediate: true })` only ever CHECKS
 * for a new service worker once, when main.ts executes — which for an
 * installed PWA just reopened from the home screen (resumed, not a fresh
 * process) may never happen again. Without a periodic/foreground re-check, a
 * shipped fix (e.g. the 2026-09-18 `public/_headers` microphone policy fix)
 * can sit undetected on Cloudflare for days while the field device keeps
 * serving an old cached build. This schedules `registration.update()` on an
 * interval AND every time the tab/app becomes visible again — it only ever
 * CHECKS, never applies (applying stays gated behind UpdateBanner.vue's tap,
 * same reasoning as above).
 */
import { ref } from 'vue';

export const needRefresh = ref(false);

let updateSWFn: ((reloadPage?: boolean) => Promise<void>) | null = null;

export function setUpdateHandler(fn: (reloadPage?: boolean) => Promise<void>): void {
  updateSWFn = fn;
}

export async function applyUpdate(): Promise<void> {
  if (updateSWFn) {
    await updateSWFn(true);
  }
}

/** How often to poll for a new service worker while the app stays open/resumed. */
export const UPDATE_CHECK_INTERVAL_MS = 20 * 60 * 1000;

/**
 * `registration.update()` only CHECKS for a new service worker (fires
 * onNeedRefresh if one is found) — it never applies one. Called on an
 * interval and on every foreground so a waiting update is discovered
 * promptly instead of only at the next cold start.
 */
export function scheduleUpdateChecks(registration: ServiceWorkerRegistration): void {
  const check = (): void => {
    void registration.update();
  };
  setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}
